/**
 * Quimby Table column definitions for the Buildings listing.
 *
 * Maps the canonical column keys from buildingsColumnPolicy.ts to
 * TableColumn<BuildingRow> descriptors.
 *
 * Column ordering matches ALL_BUILDING_COLUMNS in buildingsColumnPolicy.ts.
 * The name column is the primary (sticky-left) column.
 *
 * Sortability (IP-15589/15590):
 *  - siteId and inspectionsCount are NOT sortable (no sort_table branch).
 *  - All other columns are sortable via Table `sortableColumns`, not a
 *    per-column `sortable` field (TableColumn has no such key).
 *
 * Column visibility is handled externally by the listing component, which
 * filters the full column list by visibleColumnKeys before passing to Table.
 *
 * IP-15576: Table applies `width` as both width and minWidth on th/td.
 * Do not invent a wrapping `width` smaller than the one-line cell.
 */
import { translate } from '@utils/i18n';
import { BuildingNameCellRenderer } from './renderers/BuildingNameCellRenderer';
import { BuildingAccountCellRenderer } from './renderers/BuildingLinkCellRenderer';
const NS = 'buildings.datatable';
/**
 * All possible building column definitions (before visibility filtering).
 * Order matches ALL_BUILDING_COLUMNS in buildingsColumnPolicy.ts.
 */
export const ALL_BUILDING_COLUMN_DEFS = [
    {
        key: 'name',
        label: translate('table.headers.name', { namespace: NS }),
        column: {
            dataKey: 'name',
            label: translate('table.headers.name', { namespace: NS }),
            width: '200px',
            columnType: 'primary',
            renderer: BuildingNameCellRenderer
        }
    },
    {
        key: 'address',
        label: translate('table.headers.address', { namespace: NS }),
        column: {
            dataKey: 'address',
            label: translate('table.headers.address', { namespace: NS }),
            width: '170px'
        }
    },
    {
        key: 'city',
        label: translate('table.headers.city', { namespace: NS }),
        column: {
            dataKey: 'city',
            label: translate('table.headers.city', { namespace: NS }),
            width: '120px'
        }
    },
    {
        key: 'state',
        label: translate('table.headers.state', { namespace: NS }),
        column: {
            dataKey: 'state',
            label: translate('table.headers.state', { namespace: NS }),
            width: '80px'
        }
    },
    {
        key: 'zip',
        label: translate('table.headers.zip', { namespace: NS }),
        column: {
            dataKey: 'zip',
            label: translate('table.headers.zip', { namespace: NS }),
            width: '100px'
        }
    },
    {
        key: 'account',
        label: translate('table.headers.account', { namespace: NS }),
        column: {
            dataKey: 'accountName',
            label: translate('table.headers.account', { namespace: NS }),
            width: '200px',
            renderer: BuildingAccountCellRenderer
        }
    },
    {
        key: 'territory',
        label: translate('table.headers.territory', { namespace: NS }),
        column: {
            dataKey: 'territory',
            label: translate('table.headers.territory', { namespace: NS }),
            width: '110px'
        }
    },
    {
        key: 'contractExpiration',
        label: translate('table.headers.contract_expiration', { namespace: NS }),
        column: {
            dataKey: 'contractExpiration',
            label: translate('table.headers.contract_expiration', { namespace: NS }),
            width: '150px'
        }
    },
    {
        key: 'buildingNumber',
        label: translate('table.headers.building_number', { namespace: NS }),
        column: {
            dataKey: 'buildingNumber',
            label: translate('table.headers.building_number', { namespace: NS }),
            width: '150px'
        }
    },
    {
        key: 'referenceNumber',
        label: translate('table.headers.reference_number', { namespace: NS }),
        column: {
            dataKey: 'referenceNumber',
            label: translate('table.headers.reference_number', { namespace: NS }),
            width: '150px'
        }
    },
    {
        key: 'siteId',
        label: translate('table.headers.site_id', { namespace: NS }),
        column: {
            dataKey: 'siteId',
            label: translate('table.headers.site_id', { namespace: NS }),
            width: '110px'
        }
    },
    {
        key: 'externalId',
        label: translate('table.headers.external_id', { namespace: NS }),
        column: {
            dataKey: 'externalId',
            label: translate('table.headers.external_id', { namespace: NS }),
            width: '180px'
        }
    },
    {
        key: 'inspectionsCount',
        label: translate('table.headers.inspections_count', { namespace: NS }),
        column: {
            dataKey: 'inspectionsCount',
            label: translate('table.headers.inspections_count', { namespace: NS }),
            width: '150px'
        }
    }
];
const COLUMN_DEF_BY_KEY = new Map(ALL_BUILDING_COLUMN_DEFS.map((d) => [d.key, d]));
/**
 * Returns ordered TableColumn definitions for the given visible column keys.
 * Preserves canonical ordering from ALL_BUILDING_COLUMN_DEFS.
 */
export function buildVisibleColumns(visibleColumnKeys) {
    const keySet = new Set(visibleColumnKeys);
    return ALL_BUILDING_COLUMN_DEFS.filter((def) => keySet.has(def.key)).map((def) => def.column);
}
/**
 * Returns ColumnVisibilityItem descriptors for the ColumnVisibilityControl,
 * filtered to the allowed keys.
 */
export function buildColumnVisibilityItems(allowedKeys, requiredKeys) {
    const allowedSet = new Set(allowedKeys);
    const requiredSet = new Set(requiredKeys);
    return ALL_BUILDING_COLUMN_DEFS.filter((def) => allowedSet.has(def.key)).map((def) => ({
        key: def.key,
        label: def.label,
        required: requiredSet.has(def.key)
    }));
}
